import { apiRequest, apiRequestWithHeaders } from "../client";
import {
  caseDocumentMimeType,
} from "@/components/case-checking/caseDocumentFile";
import type { Artifact } from "@/services/api/agentic-v2";
import { dedupeCaseDetailRequest, dedupeInflightRequest, invalidateCaseDetailRequestPrefix } from "./requestCache";

/**
 * Case Checking API Service
 *
 * Handles all API calls for the FCA compliance case checking module.
 */

// ============================================
// Types
// ============================================

export type CaseStage =
  | "ingestion"
  | "completeness"
  | "routing"
  | "assessment"
  | "grading"
  | "remedial"
  | "complete";

export type CaseGrade = "suitable" | "dev-needs" | "unclear" | "unsuitable" | "stop" | null;

export type CaseRole = "planner" | "paraplanner" | "ce";

export const CASE_ROLES: ReadonlyArray<Exclude<CaseRole, null>> = [
  "planner",
  "paraplanner",
  "ce",
];

export const CASE_ROLE_LABELS: Record<CaseRole, string> = {
  planner: "Planner",
  paraplanner: "Paraplanner",
  ce: "Client Executive",
};

export type CompletenessStatus = "complete" | "incomplete" | "pending";

export type RemedialActionPriority = "high" | "medium" | "low";

export type DocumentClassification =
  // Universal documents (required for any case)
  | "fact-find"
  | "file-note"
  | "suitability-report"
  | "csa"
  | "oxford-risk"
  | "smartsearch-idd"
  | "charges-spreadsheet"
  | "voyant-cashflow"
  | "exception-form"
  | "valuation"
  // Product/case-specific documents
  | "kiid"
  | "illustration"
  | "fe-performance"
  | "off-cip-research"
  | "source-of-funds"
  | "cgt-report"
  | "tax-calculation"
  | "segment-confirmation"
  | "sustainability-confirmation"
  | "annuity-quote"
  | "kfd"
  | "cost-comparison"
  | "ceding-valuation"
  | "br19"
  | "drawdown-illustration"
  | "expenditure-breakdown"
  | "factsheet-brochure"
  | "micap-report"
  | "application-form"
  | "trust-deed"
  | "registration-cert"
  | "trustee-fact-find"
  | "trustee-aml"
  | "mortgage-fact-find"
  | "affordability-calc"
  | "payslips"
  | "bank-statements"
  | "open-market-quotes"
  | "dip"
  | "health-questionnaire"
  | "tvas-report"
  // Generic
  | "correspondence"
  | "other"
  | null;

export type CaseType =
  | "annual-review"
  | "fund-switch"
  | "top-up"
  | "isa-gia-withdrawal"
  | "bond-withdrawal"
  | "adhoc-pension-withdrawal"
  | "platform-transfer"
  | "new-business"
  | "first-time-drawdown"
  | "br-vct-seis"
  | "new-trust"
  | "existing-trust"
  | "mortgage"
  | "annuity"
  | "unknown";

export type AssessmentStatus = "sufficient" | "insufficient" | "needs-info" | "pending";

export type RuleStatus = "passed" | "failed" | "unclear" | "pending" | "na";

export interface RedFlag {
  severity: "high" | "medium" | "low";
  category: string;
  description: string;
  location?: string;
  remedial_action?: string;
}

export interface RuleEvidence {
  objectives_documented?: string;
  financial_situation?: string;
  risk_profile?: string;
  capacity_for_loss?: string;
  knowledge_experience?: string;
  client_circumstances?: string;
  recommendations_made?: string;
  alternatives_considered?: string;
  recommendation_rationale?: string;
  objectives_linkage?: string;
  risks_explained?: string;
  fees_disclosed?: string;
  charges_cumulative?: string;
  tax_implications?: string;
  product_research?: string;
  aml_verification?: string;
  client_agreement?: string;
  disclosure_docs?: string;
  client_instruction?: string;
}

export interface AssessmentRuleResult {
  rule_id: string;
  label: string;
  /** Role accountable for this rule (drives per-role scoring). */
  role: CaseRole;
  status: RuleStatus;
  evidence?: string;
  document_refs?: string[];
  citations?: RuleCitationSnapshot[];
  /** Agent surety of the verdict: green | amber | red */
  confidence?: "green" | "amber" | "red" | null;
}

export interface MissingDocument {
  document_type: string;
  document_name: string;
  required: boolean;
  reason: string;
  /** Role accountable for producing/uploading this document. */
  role: CaseRole;
}

export interface DocumentConcern {
  /** Required slug the concern applies to (e.g. 'risk-profile'). */
  document_type: string;
  document_name: string;
  /** ID of the present document whose content triggered the concern. */
  document_id: string;
  /** 'high' | 'medium' | 'low' */
  severity: "high" | "medium" | "low";
  /** One-line description of what is partial / questionable. */
  concern: string;
  role: CaseRole;
}

/** Required document type ruled not applicable to this case (with justification). */
export interface NotApplicableRequirement {
  document_type: string;
  document_name: string;
  reason: string;
  role: CaseRole;
}

export interface SatisfiedRequirement {
  document_type: string;
  document_ids: string[];
}

export interface CompletenessCheck {
  status: CompletenessStatus;
  missing_documents: MissingDocument[];
  concerns?: DocumentConcern[];
  not_applicable?: NotApplicableRequirement[];
  satisfied_requirements?: SatisfiedRequirement[];
  present_count: number;
  required_count: number;
  summary: string | null;
  can_proceed_to_assessment: boolean;
}

export interface RemedialAction {
  id: string;
  title: string;
  description: string;
  role: CaseRole;
  priority: RemedialActionPriority;
  source: "completeness" | "assessment" | "review";
  /** Assessment rule IDs this action addresses. Empty for orphans (e.g. completeness / inconsistency). */
  rule_ids?: string[];
  /** Legacy single rule id — prefer `rule_ids`. Retained for backwards compatibility. */
  rule_id: string | null;
  group_id?: string | null;
  status: "pending" | "in_progress" | "complete";
}

/** One per-role slice of a final case review. */
export interface RoleGrade {
  role: CaseRole;
  grade: Exclude<CaseGrade, null>;
  confidence: number;
  /** One short sentence — the headline issue for this role. Null when grade is suitable. */
  primary_flag: string | null;
}

export interface AreaGrade {
  area: "kyc" | "suitability" | "supporting-docs";
  status: AssessmentStatus;
  driving_check_ids?: string[];
}

export interface CaseReview {
  role_grades: RoleGrade[];
  area_grades?: AreaGrade[];
  /** Tab 8 system roll-up before agent suitability judgment. */
  baseline_grade?: CaseGrade | null;
  /** Agent overall file grade (human review sheet). */
  final_grade?: CaseGrade | null;
  /** Worst of role_grades / final_grade (computed by backend). */
  overall_grade: CaseGrade;
  grade_rationale?: string | null;
  summary: string;
  risk_flag?: boolean;
  risk_summary?: string | null;
  /** Formal compliance queue — not remedial planner routing. */
  human_review?: "no_formal_review" | "needs_human" | "priority_human" | null;
  human_review_reasons?: string[];
  reviewed_at: string;
}

/** One rule group (display bucket) with aggregate + per-rule results. */
export interface GroupAssessment {
  group_id: string;
  group_name: string;
  status: AssessmentStatus;
  reasoning: string;
  rules: AssessmentRuleResult[];
}

export interface Assessment {
  groups: GroupAssessment[];
  rule_set_version: number | null;
}

export type CaseDocumentKeyFigureValue = string | number | boolean | null;

export interface CaseDocument {
  id: string;
  case_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  classification: DocumentClassification;
  classification_confidence: number | null;
  classification_reasoning: string | null;
  classification_error?: string | null;
  summary: string | null;
  red_flags: RedFlag[] | null;
  key_dates: Record<string, string> | null;
  key_parties: Record<string, string> | null;
  key_figures: Record<string, CaseDocumentKeyFigureValue> | null;
  rule_evidence: RuleEvidence | null;
  valid: boolean;
  validity_reasoning: string | null;
  metadata?: {
    content_readable?: boolean;
    unreadable_reason?: string | null;
    classification_source?: string;
    [key: string]: unknown;
  } | null;
  created_at: string;
}

export type ReviewOnRerunMode = "fresh" | "carry";

export type ReviewOnRerunApplyStatus = "pending" | "applied" | "failed";

export interface CaseReviewOnRerunMetadata {
  mode?: ReviewOnRerunMode;
  source_review_run_id?: string;
  apply_status?: ReviewOnRerunApplyStatus;
  apply_status_at?: string;
  error?: string;
  recorded_at?: string;
  actor_id?: string;
}

/** Optional body fields for ingest, recover, and document-queue submit when a re-run may clear guided review. */
export interface CaseCheckRerunFields {
  acknowledge_rerun?: boolean;
  review_on_rerun?: ReviewOnRerunMode;
  source_review_run_id?: string;
}

/** CRM/source linkage for cases synced from an external system (e.g. Progeny). Null for manually created cases. */
export interface CaseMetadata {
  source?: string;
  account_id?: string;
  review_id?: string;
  agentic_mission_id?: string;
  review_on_rerun?: CaseReviewOnRerunMetadata;
  [key: string]: string | CaseReviewOnRerunMetadata | undefined;
}

export type InconsistencySeverity = "low" | "medium" | "high";

export interface InconsistencyValue {
  document_id: string;
  filename: string;
  value: string;
}

export interface CaseInconsistency {
  field: string;
  check_id?: string | null;
  status?: "pass" | "fail" | "flagged" | "cannot-assess" | "na" | null;
  area?: "kyc" | "suitability" | "supporting-docs" | null;
  grade_weight?: "area_insufficient" | "development" | null;
  severity: InconsistencySeverity;
  values: InconsistencyValue[];
  message: string;
  detected_at?: string;
}

export interface TimelineEvent {
  event: string;
  date?: string | null;
  source_document_id?: string | null;
  date_basis?: string | null;
}

export interface CaseTimeline {
  narrative: string;
  events: TimelineEvent[];
  anchor_date?: string | null;
  anchor_reasoning?: string | null;
  built_at?: string;
}

/** Who the advice file covers (set at case-type detection). */
export type AdviceScope = "single" | "joint" | "household";

export interface AdvisedParty {
  name: string;
  role?: string;
}

export interface Case {
  id: string;
  company_id?: string;
  client_name: string;
  adviser: string;
  paraplanner: string | null;
  ce: string | null;
  advice_type: string | null;
  case_type: CaseType;
  case_types?: CaseType[] | null;
  transaction_subtype?: string | null;
  case_type_confidence: number | null;
  case_type_reasoning: string | null;
  pre_advice_required: boolean;
  stage: CaseStage;
  grade: CaseGrade;
  assessment: Assessment;
  completeness: CompletenessCheck | null;
  remedial_actions: RemedialAction[];
  review: CaseReview | null;
  /** KYC / suitability / supporting-docs roll-up (from API; live before grade, persisted after). */
  area_grades?: AreaGrade[];
  inconsistencies: CaseInconsistency[];
  timeline?: CaseTimeline | null;
  document_count: number;
  created_at: string;
  updated_at: string;
  metadata?: CaseMetadata | null;
  /** Tenant review profile slug (drives review-stage principles for assessment). */
  review_profile?: string | null;
  advice_scope?: AdviceScope | null;
  advised_parties?: AdvisedParty[];
  advice_scope_reasoning?: string | null;
  document_addition_queue?: DocumentAdditionQueue | null;
}

export type DocumentAdditionQueueStatus = "open" | "scheduled" | "submitting";

export type DocumentAdditionQueueItemKind = "upload" | "progeny";

export interface DocumentAdditionQueueItem {
  id: string;
  kind: DocumentAdditionQueueItemKind;
  label: string;
  document_id?: string | null;
  progeny_document_id?: string | null;
  added_at: string;
  added_by?: string | null;
}

export interface DocumentAdditionQueue {
  status: DocumentAdditionQueueStatus;
  items: DocumentAdditionQueueItem[];
  scheduled_submit_at?: string | null;
  last_submitted_at?: string | null;
  last_task_id?: string | null;
  item_count: number;
}

export interface DocumentAdditionQueueSubmitResponse {
  status: string;
  message: string;
  task_id?: string | null;
  scheduled_submit_at?: string | null;
}

export interface ReviewProfileOption {
  slug: string;
  label: string;
  description: string;
  overarching_principles: string;
  is_default: boolean;
}

/** One row from GET …/available-documents (CRM catalogue). */
export interface AvailableDocument {
  document_id: string;
  filename: string;
  subject: string;
  document_type_label: string;
  document_date: string | null;
  file_size: number | null;
  mime_type: string;
  has_download_url: boolean;
  selected_for_case: boolean;
  suggested?: boolean;
  list_section?: string | null;
}

/** Response for listing all CRM documents available for a case account. */
export interface AvailableDocumentsResponse {
  case_id: string;
  source_system: string | null;
  source_account_id: string | null;
  documents: AvailableDocument[];
  total_count: number;
  selected_count: number;
  suggested_count?: number;
  cached_at: string | null;
  source: "cached" | "live";
  project_key?: string | null;
  review_label?: string | null;
}

export interface CaseCreateRequest {
  client_name?: string;
  adviser?: string;
  paraplanner?: string;
  advice_type?: string;
  pre_advice_required?: boolean;
  review_profile?: string;
}

export const listReviewProfiles = async (): Promise<ReviewProfileOption[]> => {
  return apiRequest<ReviewProfileOption[]>({
    method: "GET",
    url: `/case-checking/review-profiles`,
  });
};

// ============================================
// Case Operations
// ============================================

/**
 * Create a new case.
 */
export const createCase = async (data: CaseCreateRequest): Promise<Case> => {
  const response = await apiRequest<Case>({
    method: "POST",
    url: `/case-checking/cases`,
    data,
  });
  return response;
};

/**
 * Get a paginated list of cases with optional filtering.
 */
export interface CaseListPage {
  items: Case[];
  total: number;
  skip: number;
  limit: number;
}

export const CASE_LIST_PAGE_SIZE = 100;

export const getCasesPage = async (params?: {
  stage?: CaseStage;
  grade?: CaseGrade;
  skip?: number;
  limit?: number;
  company_id?: string;
}): Promise<CaseListPage> => {
  const skip = params?.skip ?? 0;
  const limit = params?.limit ?? 50;
  const cacheKey = `cases-page:${skip}:${limit}:${params?.stage ?? ""}:${params?.grade ?? ""}:${params?.company_id ?? ""}`;
  return dedupeInflightRequest(cacheKey, () =>
    apiRequest<CaseListPage>({
      method: "GET",
      url: `/case-checking/cases`,
      params: {
        skip,
        limit,
        stage: params?.stage,
        grade: params?.grade,
        company_id: params?.company_id,
      },
    }),
  );
};

/**
 * Get a list of cases (single page items only).
 * @deprecated Prefer getCasesPage for pagination metadata.
 */
export const getCases = async (params?: {
  stage?: CaseStage;
  grade?: CaseGrade;
  skip?: number;
  limit?: number;
}): Promise<Case[]> => {
  const page = await getCasesPage(params);
  return page.items;
};

function uniqueCasesById(items: Case[]): Case[] {
  const seen = new Set<string>();
  const out: Case[] = [];
  for (const item of items) {
    if (!item.id || seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

/** Fetch every case page from the API. */
export const getAllCases = async (params?: {
  stage?: CaseStage;
  grade?: CaseGrade;
}): Promise<Case[]> => {
  const all: Case[] = [];
  let skip = 0;
  for (;;) {
    const page = await getCasesPage({ ...params, skip, limit: CASE_LIST_PAGE_SIZE });
    if (!page.items.length) break;
    all.push(...page.items);
    if (skip + page.items.length >= page.total) break;
    skip += CASE_LIST_PAGE_SIZE;
  }
  return uniqueCasesById(all);
};

/**
 * Get a single case by ID.
 */
export const getCase = async (caseId: string): Promise<Case> => {
  return dedupeCaseDetailRequest(`case:${caseId}`, () =>
    apiRequest<Case>({
      method: "GET",
      url: `/case-checking/cases/${caseId}`,
    }),
  );
};

export interface MissionStageProgress {
  name: string;
  display_name: string;
  order: number;
  status: string;
}

export interface CaseAgenticMissionLink {
  mission_id: string;
  /** Open agentic v2 interaction task (Mission Control), not an api-runner task. */
  active_task_id: string | null;
  /** In-flight case_check_ingest / case_check_pipeline api-runner task, when any. */
  runner_task_id?: string | null;
  /** In-flight mission-tool progress task (background_tasks /ws/tasks), when any. */
  progress_task_id?: string | null;
  mission_status: string | null;
  current_stage?: string | null;
  current_stage_display?: string | null;
  progress_pct?: number | null;
  stages?: MissionStageProgress[];
  current_stage_order?: number | null;
  stage_count?: number | null;
  is_active?: boolean;
  /** True when an agentic, runner, or mission-tool task is actively executing. */
  has_running_work?: boolean;
  agentic_task_status?: string | null;
  blocked_kind?: string | null;
  latest_summary?: string | null;
  needs_attention?: boolean;
  attention_kind?: string | null;
  attention_summary?: string | null;
  allowed_actions?: string[];
  mission_href?: string | null;
  mission_control_href?: string | null;
  chain_failed?: boolean;
  can_retry_chain?: boolean;
  intake_failed?: boolean;
  can_retry_intake?: boolean;
  last_failed_runner_task_id?: string | null;
  /** Human label from task_catalogue for the open agentic task, when resolved. */
  active_task_display_name?: string | null;
  active_task_catalogue_key?: string | null;
}

export interface RecoverCaseCheckResponse {
  status: string;
  message: string;
  spawned_task_ids?: string[];
  actions?: string[];
}

export const recoverCaseCheck = async (
  caseId: string,
  body: CaseCheckRerunFields = {},
): Promise<RecoverCaseCheckResponse> => {
  return apiRequest<RecoverCaseCheckResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/recover-case-check`,
    data: body,
  });
};

export interface SkipCaseCheckAdjudicationResponse {
  task_id: string;
  mission_id: string;
  message: string;
}

export const skipCaseCheckAdjudication = async (caseId: string): Promise<SkipCaseCheckAdjudicationResponse> => {
  return apiRequest<SkipCaseCheckAdjudicationResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/skip-adjudication`,
  });
};

export interface StopCaseProcessingResponse {
  action: string;
  cancelled_runner_task_ids?: string[];
  cancelled_task_id?: string | null;
  mission_id?: string | null;
}

export const stopCaseProcessing = async (caseId: string): Promise<StopCaseProcessingResponse> => {
  return apiRequest<StopCaseProcessingResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/stop-processing`,
  });
};

/** Agentic mission linked via ``mission.context.case_id``, if any. */
export const getCaseAgenticMission = async (
  caseId: string,
): Promise<CaseAgenticMissionLink | null> => {
  return apiRequest<CaseAgenticMissionLink | null>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/agentic-mission`,
  });
};

/** Batch mission status for the cases list (Marvin progress). */
export const listCaseAgenticMissionStatuses = async (
  caseIds: string[],
): Promise<Record<string, CaseAgenticMissionLink>> => {
  if (caseIds.length === 0) return {};
  const sorted = [...caseIds].sort();
  const cacheKey = `mission-status:${sorted.join(",")}`;
  return dedupeInflightRequest(cacheKey, async () => {
    const response = await apiRequest<{ items: Record<string, CaseAgenticMissionLink> }>({
      method: "GET",
      url: "/case-checking/cases/agentic-mission-status",
      params: { case_ids: sorted.join(",") },
    });
    return response.items ?? {};
  });
};

export interface ProgenyAccountLookup {
  account_id: string;
  name: string | null;
}

/** Resolve a Curo account ID to client name before launch. */
export const lookupProgenyAccount = async (
  accountId: string,
): Promise<ProgenyAccountLookup> => {
  return apiRequest<ProgenyAccountLookup>({
    method: "GET",
    url: `/case-checking/progeny/accounts/${encodeURIComponent(accountId)}`,
  });
};

export interface LaunchProgenyCaseCheckRequest {
  progeny_account_id: string;
  client_name?: string | null;
  progeny_review_id?: string | null;
}

export interface LaunchProgenyCaseCheckResponse {
  mission_id: string;
  task_id: string | null;
  case_id: string;
}

/** Launch Marvin progeny-pull from Case Checking (no Mission Control navigation required). */
export const launchProgenyCaseCheck = async (
  body: LaunchProgenyCaseCheckRequest,
): Promise<LaunchProgenyCaseCheckResponse> => {
  return apiRequest<LaunchProgenyCaseCheckResponse>({
    method: "POST",
    url: "/case-checking/progeny/launch-case-check",
    data: body,
  });
};

export interface SoderbergPlannrFirm {
  key: string;
  name: string;
}

export interface SoderbergPlannrClientRow {
  uuid: string;
  name: string;
}

export interface SoderbergPlannrClientSearch {
  firm_key: string;
  firm_name: string;
  clients: SoderbergPlannrClientRow[];
}

export const listSoderbergPlannrFirms = async (): Promise<SoderbergPlannrFirm[]> => {
  return apiRequest<SoderbergPlannrFirm[]>({
    method: "GET",
    url: "/case-checking/plannr/firms",
  });
};

export const searchSoderbergPlannrClients = async (
  firm: string,
  q: string,
): Promise<SoderbergPlannrClientSearch> => {
  return apiRequest<SoderbergPlannrClientSearch>({
    method: "GET",
    url: "/case-checking/plannr/clients",
    params: { firm, q },
  });
};

export interface LaunchSoderbergPlannrCaseCheckRequest {
  firm_key: string;
  plannr_client_uuid: string;
  client_name?: string | null;
}

export const launchSoderbergPlannrCaseCheck = async (
  body: LaunchSoderbergPlannrCaseCheckRequest,
): Promise<LaunchProgenyCaseCheckResponse> => {
  return apiRequest<LaunchProgenyCaseCheckResponse>({
    method: "POST",
    url: "/case-checking/plannr/launch-case-check",
    data: body,
  });
};

/**
 * Update a case.
 */
export const updateCase = async (
  caseId: string,
  data: Partial<CaseCreateRequest> & { stage?: CaseStage; grade?: CaseGrade }
): Promise<Case> => {
  const response = await apiRequest<Case>({
    method: "PATCH",
    url: `/case-checking/cases/${caseId}`,
    data,
  });
  return response;
};

/**
 * Delete a case.
 */
export const deleteCase = async (caseId: string): Promise<void> => {
  await apiRequest({
    method: "DELETE",
    url: `/case-checking/cases/${caseId}`,
  });
};

// ============================================
// Document Operations
// ============================================

/**
 * Upload a document to a case.
 * Returns the document record (classification runs in agentic triage, not on upload).
 */
export const uploadCaseDocument = async (
  caseId: string,
  file: File,
  options?: { queueAddition?: boolean },
): Promise<CaseDocument> => {
  const formData = new FormData();
  formData.append("file", file);
  const queueAddition = options?.queueAddition ?? false;

  const response = await apiRequest<CaseDocument>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/documents`,
    data: formData,
    params: queueAddition ? { queue_addition: true } : undefined,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

/**
 * Get all documents for a case.
 */
export const getCaseDocuments = async (
  caseId: string
): Promise<CaseDocument[]> => {
  return dedupeCaseDetailRequest(`case-documents:${caseId}`, () =>
    apiRequest<CaseDocument[]>({
      method: "GET",
      url: `/case-checking/cases/${caseId}/documents`,
    }),
  );
};

/**
 * List CRM documents available for the case’s linked account (cached by default).
 *
 * @param caseId - Case id
 * @param options.search - Filter by filename/subject (case-insensitive)
 * @param options.refresh - Force live fetch from CRM (slow, ~20–30s for large accounts)
 */
export const getAvailableDocuments = async (
  caseId: string,
  options?: { search?: string; refresh?: boolean; scope?: "suggested" | "all" }
): Promise<AvailableDocumentsResponse> => {
  return apiRequest<AvailableDocumentsResponse>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/available-documents`,
    params: {
      ...(options?.search ? { search: options.search } : {}),
      ...(options?.refresh ? { refresh: true } : {}),
      ...(options?.scope ? { scope: options.scope } : {}),
    },
  });
};

/**
 * Batch-import CRM documents onto the case.
 */
export const importDocumentsFromProgeny = async (
  caseId: string,
  documentIds: string[],
  options?: { classifyOnImport?: boolean },
): Promise<{ imported: string[]; skipped: string[]; failed: string[] }> => {
  return apiRequest({
    method: "POST",
    url: `/case-checking/cases/${caseId}/documents/import-from-progeny`,
    data: { document_ids: documentIds },
    params: options?.classifyOnImport ? { classify_on_import: true } : undefined,
  });
};

/**
 * Import a CRM document into the case by CRM document id (from {@link getAvailableDocuments}).
 */
export const addDocumentFromProgeny = async (
  caseId: string,
  documentId: string,
  options?: { classifyOnImport?: boolean },
): Promise<CaseDocument> => {
  return apiRequest<CaseDocument>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/documents/add-from-progeny`,
    data: { document_id: documentId },
    params: options?.classifyOnImport ? { classify_on_import: true } : undefined,
  });
};

/**
 * Get a single document by ID.
 */
export const getCaseDocument = async (
  caseId: string,
  documentId: string
): Promise<CaseDocument> => {
  const response = await apiRequest<CaseDocument>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/documents/${documentId}`,
  });
  return response;
};

/**
 * Trigger re-classification for a document.
 */
export const classifyDocument = async (
  caseId: string,
  documentId: string,
): Promise<CaseDocument> => {
  const response = await apiRequest<CaseDocument>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/documents/${documentId}/classify`,
  });
  return response;
};

/**
 * Delete a document from a case.
 */
export const deleteCaseDocument = async (
  caseId: string,
  documentId: string
): Promise<void> => {
  await apiRequest({
    method: "DELETE",
    url: `/case-checking/cases/${caseId}/documents/${documentId}`,
  });
};

export {
  caseDocumentCanViewInline,
  caseDocumentExtension,
  caseDocumentMimeType,
} from "@/components/case-checking/caseDocumentFile";

/** Fetch raw document bytes from case-checking storage. */
export const fetchCaseDocumentBlob = async (
  caseId: string,
  documentId: string,
  inline: boolean = false,
): Promise<Blob> => {
  const qs = inline ? "?disposition=inline" : "";
  const response = await apiRequestWithHeaders<Blob>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/documents/${documentId}/file${qs}`,
    responseType: "blob",
  });
  return response.data;
};

/** Download the original case document file. */
export const downloadCaseDocumentFile = async (
  caseId: string,
  doc: Pick<CaseDocument, "id" | "filename" | "file_type">,
): Promise<void> => {
  const blob = await fetchCaseDocumentBlob(caseId, doc.id, false);
  const mime = caseDocumentMimeType(doc.file_type, doc.filename);
  const url = URL.createObjectURL(new Blob([blob], { type: mime }));
  const a = window.document.createElement("a");
  a.href = url;
  a.download = doc.filename;
  window.document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Open the original case document in a new browser tab (PDF/images inline). */
export const viewCaseDocumentFile = async (
  caseId: string,
  doc: Pick<CaseDocument, "id" | "filename" | "file_type">,
): Promise<void> => {
  const blob = await fetchCaseDocumentBlob(caseId, doc.id, true);
  const mime = caseDocumentMimeType(doc.file_type, doc.filename);
  const url = URL.createObjectURL(new Blob([blob], { type: mime }));
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// ============================================
// Background task operations
// ============================================

export type CaseCheckIngestSource =
  | "progeny_pull"
  | "progeny"
  | "classify_only"
  | "review_folder"
  | "review_supplements"
  | "uploaded";

export type CaseCheckPipelineStep = "detect_type" | "completeness" | "assess" | "review";

export interface CaseCheckTaskResponse {
  task_id: string;
  status: string;
  message: string;
}

export interface CaseCheckActiveTaskResponse {
  task_id: string | null;
}

export interface TriggerCaseCheckIngestRequest extends CaseCheckRerunFields {
  source?: CaseCheckIngestSource;
  progeny_document_ids?: string[];
  document_ids?: string[];
  resume_mission_node?: string;
}

export interface TriggerCaseCheckPipelineRequest {
  steps?: CaseCheckPipelineStep[];
  use_llm?: boolean;
}

export const triggerCaseCheckIngest = async (
  caseId: string,
  body: TriggerCaseCheckIngestRequest,
): Promise<CaseCheckTaskResponse> => {
  return apiRequest<CaseCheckTaskResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/ingest`,
    data: body,
  });
};

export const triggerCaseCheckPipeline = async (
  caseId: string,
  body: TriggerCaseCheckPipelineRequest = {},
): Promise<CaseCheckTaskResponse> => {
  return apiRequest<CaseCheckTaskResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/pipeline`,
    data: body,
  });
};

export const getCaseActiveTask = async (
  caseId: string,
): Promise<CaseCheckActiveTaskResponse> => {
  return apiRequest<CaseCheckActiveTaskResponse>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/active-task`,
  });
};

export const getDocumentAdditionQueue = async (caseId: string): Promise<DocumentAdditionQueue> => {
  return apiRequest<DocumentAdditionQueue>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/document-addition-queue`,
  });
};

export const addProgenyItemsToDocumentQueue = async (
  caseId: string,
  body: { progeny_document_ids: string[]; labels?: Record<string, string> },
): Promise<DocumentAdditionQueue> => {
  return apiRequest<DocumentAdditionQueue>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/document-addition-queue/progeny-items`,
    data: body,
  });
};

export const removeDocumentQueueItem = async (caseId: string, itemId: string): Promise<DocumentAdditionQueue> => {
  return apiRequest<DocumentAdditionQueue>({
    method: "DELETE",
    url: `/case-checking/cases/${caseId}/document-addition-queue/items/${itemId}`,
  });
};

export interface DocumentAdditionQueueSubmitRequest extends CaseCheckRerunFields {
  mode: "now" | "tonight";
}

export const submitDocumentAdditionQueue = async (
  caseId: string,
  mode: "now" | "tonight",
  rerunFields: CaseCheckRerunFields = {},
): Promise<DocumentAdditionQueueSubmitResponse> => {
  return apiRequest<DocumentAdditionQueueSubmitResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/document-addition-queue/submit`,
    data: { mode, ...rerunFields },
  });
};

export const cancelScheduledDocumentQueue = async (caseId: string): Promise<DocumentAdditionQueue> => {
  return apiRequest<DocumentAdditionQueue>({
    method: "DELETE",
    url: `/case-checking/cases/${caseId}/document-addition-queue/schedule`,
  });
};

// ============================================
// Assessment Operations
// ============================================

/**
 * Detect the case type based on uploaded documents.
 * Uses AI to analyze document content and determine the type of advice.
 */
export const detectCaseType = async (caseId: string): Promise<Case> => {
  const response = await apiRequest<Case>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/detect-type`,
  });
  return response;
};

/**
 * Check document completeness for a case.
 * Compares uploaded documents against requirements for the detected case type.
 * Should be run AFTER case type detection and BEFORE assessment.
 */
export const checkCompleteness = async (caseId: string): Promise<Case> => {
  const response = await apiRequest<Case>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/check-completeness`,
  });
  return response;
};

/**
 * Run compliance assessment on the case.
 * Analyzes all documents against the 33-rule FCA compliance rubric.
 *
 * @param caseId - The case to assess
 * @param useLlm - If true (default), uses AI for deep analysis. If false, only checks document presence.
 */
export const runAssessment = async (
  caseId: string,
  useLlm: boolean = true
): Promise<Case> => {
  const response = await apiRequest<Case>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/assess`,
    params: { use_llm: useLlm },
  });
  return response;
};

/**
 * Generate final review and grade recommendation for a case.
 * Analyzes completeness, assessment results, and documents.
 * Should be run AFTER assessment is complete.
 */
export const generateReview = async (caseId: string): Promise<Case> => {
  const response = await apiRequest<Case>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/generate-review`,
  });
  return response;
};

// ============================================
// Peer Review Comparison
// ============================================

export type PeerReviewAgreement = "agree" | "partial" | "disagree";
export type PeerReviewSource = "demo" | "upload";
export type PeerReviewWhoIsRight = "agent" | "human" | "both" | "unclear";
export type AgentReviewQuality = "strong" | "adequate" | "needs-improvement" | "";
export type HumanRowVerdict = "agent" | "human" | "partial" | "";

export interface PeerReviewDivergence {
  row_id: string;
  role: CaseRole;
  topic: string;
  agent_view: string;
  human_view: string;
  who_is_right: PeerReviewWhoIsRight;
  explanation: string;
  rule_reference: string | null;
}

/** Per-role agent vs human grade comparison row. */
export interface RolePeerGrades {
  role: CaseRole;
  human_grade: Exclude<CaseGrade, null>;
  agent_grade: Exclude<CaseGrade, null>;
  agreement: PeerReviewAgreement;
}

export interface FinalHumanAssessment {
  grade: CaseGrade | null;
  approved: boolean;
  notes: string;
  approved_at: string | null;
}

export interface PeerReviewerSummary {
  overall_notes: string;
  agent_review_quality: AgentReviewQuality;
  final_assessment: FinalHumanAssessment;
  saved_at: string | null;
}

export interface PeerReviewComparison {
  case_title: string;
  case_type_label: string;
  role_grades: RolePeerGrades[];
  /** Worst of role_grades.human_grade (computed by backend). */
  overall_human_grade: CaseGrade;
  /** Worst of role_grades.agent_grade (computed by backend). */
  overall_agent_grade: CaseGrade;
  /** Top-line agreement derived from per-role agreement. */
  overall_agreement: PeerReviewAgreement;
  divergences: PeerReviewDivergence[];
  learnings: string[];
  source: PeerReviewSource;
  /** Set on POST response after the run is persisted. */
  run_id?: string;
  created_at?: string;
  reviewer_row_notes?: Record<string, string>;
  reviewer_row_verdicts?: Record<string, HumanRowVerdict>;
  reviewer_summary?: PeerReviewerSummary;
  learnings_refreshed_at?: string | null;
}

/** Metadata for inputs supplied with an upload-mode comparison (no raw file bodies). */
export interface PeerReviewInputFileEntry {
  filename: string;
  size_bytes: number;
  mime_type: string;
}

export interface PeerReviewInputManifest {
  files: PeerReviewInputFileEntry[];
  human_review_text_char_count: number;
  human_review_text_sha256: string | null;
}

/** One saved peer-review comparison run (GET list/detail). */
export interface PeerReviewRun {
  id: string;
  case_id: string;
  run_by: string | null;
  source: PeerReviewSource;
  comparison: PeerReviewComparison;
  input_manifest: PeerReviewInputManifest;
  reviewer_row_notes: Record<string, string>;
  reviewer_row_verdicts: Record<string, HumanRowVerdict>;
  reviewer_summary: PeerReviewerSummary;
  learnings_refreshed_at: string | null;
  created_at: string;
}

/**
 * Run a peer-review comparison for a case.
 *
 * - source="upload" (default) → uses any combination of pasted text and/or
 *   uploaded files (.pdf / .md / .txt). PDFs are forwarded to Claude as
 *   native document attachments.
 * - source="demo" → LLM simulates a plausible peer reviewer (kept for backward
 *   compatibility; not used by the current UI).
 */
export const runPeerReviewComparison = async (
  caseId: string,
  options: {
    source?: PeerReviewSource;
    humanReviewText?: string;
    files?: File[];
  }
): Promise<PeerReviewComparison> => {
  const formData = new FormData();
  formData.append("source", options.source ?? "upload");
  if (options.humanReviewText) {
    formData.append("human_review_text", options.humanReviewText);
  }
  for (const file of options.files ?? []) {
    formData.append("files", file);
  }
  return apiRequest<PeerReviewComparison>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/peer-review-comparison`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** Streams a GET blob from case-checking and triggers browser download (shared helper). */
const _downloadCaseCheckingBlob = async (
  path: string,
  fallbackName: string,
  mime: string
): Promise<void> => {
  const response = await apiRequestWithHeaders<Blob>({
    method: "GET",
    url: path,
    responseType: "blob",
  });

  let filename = fallbackName;
  const disposition = response.headers["content-disposition"] as string | undefined;
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/i);
    if (match?.[1]) {
      filename = match[1];
    }
  }

  const blob = new Blob([response.data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Download peer-review XLSX workbook for a case and trigger a browser download.
 *
 * The workbook contains an Overview sheet plus one sheet per assessment group,
 * with empty Human Verdict / Human Notes columns for the reviewer to fill in.
 */
export const downloadPeerReviewXlsx = async (
  caseId: string,
  fallbackName: string = "peer_review.xlsx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/peer-review-xlsx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/**
 * Download leadership summary Word document (short narrative for CRO / leadership).
 */
export const downloadLeadershipSummaryDocx = async (
  caseId: string,
  fallbackName: string = "leadership_summary.docx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/leadership-summary-docx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
};

/** Open a case-checking export PDF in a new browser tab (LibreOffice conversion on the API). */
export const openCaseCheckingExportPdf = async (url: string): Promise<void> => {
  const response = await apiRequestWithHeaders<Blob>({
    method: "GET",
    url,
    responseType: "blob",
  });
  const blob = new Blob([response.data], { type: "application/pdf" });
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

function caseCheckingDocxExportPdfUrl(
  caseId: string,
  exportPath: string,
  options?: { runId?: string; reviewRunId?: string },
): string {
  const params = new URLSearchParams({ format: "pdf" });
  if (options?.runId) params.set("run_id", options.runId);
  if (options?.reviewRunId) params.set("review_run_id", options.reviewRunId);
  return `/case-checking/cases/${caseId}/export/${exportPath}?${params.toString()}`;
}

/**
 * Download case + document register Excel workbook (overview + documents sheet).
 */
export const downloadCaseRegisterXlsx = async (
  caseId: string,
  fallbackName: string = "case_register.xlsx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/case-register-xlsx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/** Deterministic completeness checklist (.xlsx) with red / amber / green rows. */
export const downloadCompletenessXlsx = async (
  caseId: string,
  fallbackName: string = "completeness.xlsx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/completeness-xlsx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/** All assessment rules with RAG and evidence (.xlsx). */
export const downloadAssessmentRulesXlsx = async (
  caseId: string,
  fallbackName: string = "assessment_rules.xlsx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/assessment-rules-xlsx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/** Remedial actions with priority RAG (.xlsx). */
export const downloadRemedialsXlsx = async (
  caseId: string,
  fallbackName: string = "remedials.xlsx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/remedials-xlsx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/** Rules audit: rule set + agent results + yellow reviewer columns (.xlsx). */
export const downloadRulesAuditXlsx = async (
  caseId: string,
  fallbackName: string = "rules_audit.xlsx"
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/rules-audit-xlsx`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/** Artefacts attached to this compliance case (and linked mission). */
export const listCaseArtifacts = async (caseId: string): Promise<Artifact[]> => {
  return apiRequest<Artifact[]>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/artifacts`,
  });
};

/**
 * List saved peer-review comparison runs for a case (newest first).
 * Used to restore the latest result when reopening the Peer Review tab.
 */
export const listPeerReviewRuns = async (
  caseId: string,
  limit: number = 50
): Promise<PeerReviewRun[]> => {
  return apiRequest<PeerReviewRun[]>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/peer-review-runs`,
    params: { limit },
  });
};

/**
 * Merge per-row reviewer notes and/or save overall reviewer summary (no LLM).
 * Empty string for a row_id removes that note.
 */
export const patchPeerReviewRunNotes = async (
  caseId: string,
  runId: string,
  patch: {
    reviewerRowNotes?: Record<string, string>;
    reviewerRowVerdicts?: Record<string, HumanRowVerdict>;
    reviewerSummary?: Partial<PeerReviewerSummary>;
  }
): Promise<PeerReviewRun> => {
  const body: Record<string, unknown> = {};
  if (patch.reviewerRowNotes !== undefined) {
    body.reviewer_row_notes = patch.reviewerRowNotes;
  }
  if (patch.reviewerRowVerdicts !== undefined) {
    body.reviewer_row_verdicts = patch.reviewerRowVerdicts;
  }
  if (patch.reviewerSummary !== undefined) {
    body.reviewer_summary = patch.reviewerSummary;
  }
  return apiRequest<PeerReviewRun>({
    method: "PATCH",
    url: `/case-checking/cases/${caseId}/peer-review-runs/${runId}/notes`,
    data: body,
  });
};

/**
 * Regenerate comparison learnings from stored divergences + current reviewer notes.
 */
export const refreshPeerReviewLearnings = async (
  caseId: string,
  runId: string
): Promise<PeerReviewRun> => {
  return apiRequest<PeerReviewRun>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/peer-review-runs/${runId}/refresh-learnings`,
  });
};

// ============================================
// Adjudication (human sheet vs agent)
// ============================================

export type AdjudicationCall = "pass" | "fail" | "na" | "unclear";
export type AdjudicationLenience = "human_more_lenient" | "agent_more_lenient" | "equal";
export type WhoIsRight = "agent" | "human" | "partial" | "unclear";
export type AdjudicatorGradeCall = "agree_agent" | "agree_human" | "partial" | "open";

export interface AdjudicationHeader {
  client_name: string;
  advice_summary: string;
  human_sheet_reviewer?: string | null;
  human_sheet_date?: string | null;
  human_benchmark_source?: string | null;
}

export interface AdjudicationRollup {
  human_overall_grade?: string | null;
  agent_final_grade?: string | null;
  human_review_routing?: string | null;
  adjudicator_grade_call: AdjudicatorGradeCall;
  notes: string;
}

export interface AdjudicationRow {
  row_id: string;
  rule_id?: string | null;
  rule_label: string;
  area: string;
  human_position: string;
  agent_position: string;
  human_call: AdjudicationCall;
  agent_call: AdjudicationCall;
  lenience: AdjudicationLenience;
  who_is_right: WhoIsRight;
  file_evidence: string;
  notes: string;
  document_refs: string[];
}

export interface AdjudicationRun {
  id: string;
  case_id: string;
  run_by: string | null;
  header: AdjudicationHeader;
  rollup: AdjudicationRollup;
  rows: AdjudicationRow[];
  created_at: string;
}

export interface HumanSheetSummary {
  has_sheet: boolean;
  row_count: number;
  client_name?: string | null;
  human_sheet_reviewer?: string | null;
  human_sheet_date?: string | null;
  human_overall_grade?: string | null;
}

export interface AdjudicationTriggerResult {
  task_id: string;
  mission_id: string;
}

export interface CaseReviewAdjudicationReadiness {
  has_active_review: boolean;
  review_complete: boolean;
  disagreement_count: number;
  can_trigger: boolean;
  message?: string | null;
  reviewer_email?: string | null;
  guided_review_run_id?: string | null;
  /** True when a saved adjudication run no longer matches current rule disagreements from human review. */
  adjudication_stale?: boolean;
}

/** Newest adjudicate mission task for a case (agentic task status, e.g. queued / working / done / blocked). */
export interface AdjudicationTask {
  task_id: string;
  mission_id: string;
  status: string;
  reason?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export const getAdjudicationTask = async (caseId: string): Promise<AdjudicationTask | null> => {
  return apiRequest<AdjudicationTask | null>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/adjudication/task`,
  });
};

export const getAdjudicationHumanSheet = async (
  caseId: string,
): Promise<HumanSheetSummary> => {
  return apiRequest<HumanSheetSummary>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/adjudication/human-sheet`,
  });
};

export const uploadAdjudicationHumanSheet = async (
  caseId: string,
  file: File,
): Promise<HumanSheetSummary> => {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<HumanSheetSummary>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/adjudication/human-sheet`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const triggerAdjudication = async (
  caseId: string,
): Promise<AdjudicationTriggerResult> => {
  return apiRequest<AdjudicationTriggerResult>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/adjudication/trigger`,
  });
};

export const getCaseReviewAdjudicationReadiness = async (
  caseId: string,
): Promise<CaseReviewAdjudicationReadiness> => {
  return apiRequest<CaseReviewAdjudicationReadiness>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/adjudication/case-review-readiness`,
  });
};

export const triggerAdjudicationFromCaseReview = async (
  caseId: string,
): Promise<AdjudicationTriggerResult> => {
  return apiRequest<AdjudicationTriggerResult>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/adjudication/trigger-from-case-review`,
  });
};

export const listAdjudicationRuns = async (
  caseId: string,
  limit = 20,
): Promise<AdjudicationRun[]> => {
  return apiRequest<AdjudicationRun[]>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/adjudication-runs`,
    params: { limit },
  });
};

export const getAdjudicationRun = async (
  caseId: string,
  runId: string,
): Promise<AdjudicationRun> => {
  return apiRequest<AdjudicationRun>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/adjudication-runs/${runId}`,
  });
};

/** Download adjudication worksheet (.xlsx) for offline human sign-off. */
export const downloadAdjudicationWorksheetXlsx = async (
  caseId: string,
  fallbackName: string = "adjudication_worksheet.xlsx",
  runId?: string,
): Promise<void> => {
  const query = runId ? `?run_id=${encodeURIComponent(runId)}` : "";
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/adjudication-worksheet-xlsx${query}`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  );
};

// ============================================
// Problem statements (Progeny coverage review)
// ============================================

export type ProblemStatementRating = "pass" | "fail" | "unclear" | "na" | "informational";
export type ProblemStatementConfidence = "high" | "medium" | "low";
export type HumanStatementCall = "yes" | "unclear" | "no" | "na";
export type RuleHumanCall = "pass" | "fail" | "unclear" | "na" | "excluded";

export interface RuleCitationSnapshot {
  document_id: string;
  cited_text: string;
  start?: number | null;
  end?: number | null;
  kind?: string | null;
}

export interface ProblemStatementRuleSnapshot {
  rule_id: string;
  label: string;
  status: string;
  evidence: string;
  document_refs: string[];
  citations?: RuleCitationSnapshot[];
  confidence?: string | null;
  ownership: "primary" | "secondary";
}

export interface ProblemStatementRollup {
  id: string;
  title: string;
  assessment_question: string;
  rollup_kind: "rules" | "derived" | "display_only" | "final";
  rating: ProblemStatementRating;
  rules: ProblemStatementRuleSnapshot[];
  counts: Record<string, number>;
  /** Outcomes-framework gateway (Progeny); absent for tenants without gateways. */
  gateway?: number | null;
  gateway_name?: string;
}

export interface ProblemStatementNarrative {
  problem_statement_id: string;
  confidence: ProblemStatementConfidence;
  /** One short natural sentence for summary list skim. */
  skim_answer?: string;
  summary: string;
  concerns: string[];
}

export interface ProblemStatementHumanReview {
  problem_statement_id: string;
  call: HumanStatementCall;
  notes: string;
  reviewer_name: string;
  reviewed_at: string | null;
  cleared_at?: string | null;
  cleared_by?: string;
  previous_call?: HumanStatementCall | null;
  previous_notes?: string;
  previous_reviewer_name?: string;
  previous_reviewed_at?: string | null;
}

/** PATCH body item — `clear: true` withdraws an active review back to pending. */
export type ProblemStatementHumanReviewPatch = ProblemStatementHumanReview & {
  clear?: boolean;
};

export interface ProblemStatementRun {
  id: string;
  case_id: string;
  run_by: string | null;
  catalog_version: number;
  rollups: ProblemStatementRollup[];
  narratives: ProblemStatementNarrative[];
  human_reviews: ProblemStatementHumanReview[];
  created_at: string;
}

export interface PsRuleHumanReview {
  problem_statement_id: string;
  rule_id: string;
  call: RuleHumanCall;
  notes: string;
  reviewer_name: string;
  reviewed_at: string | null;
}

export type PsRuleHumanReviewPatch = PsRuleHumanReview & { clear?: boolean };

export type GuidedReviewStep =
  | "narrative"
  | "documents"
  | "checklist"
  | "rules"
  | "assessment"
  | "actions"
  | "summary";

export interface GuidedCompletenessReview {
  status: "agree" | "disagree";
  note: string;
}

export interface GuidedReviewStepCompletion {
  step: GuidedReviewStep;
  user_id: string;
  user_email: string;
  completed_at: string;
}

export interface GuidedReviewAuditEvent {
  at: string;
  user_id: string;
  user_email: string;
  kind: string;
  detail: Record<string, unknown>;
}

export interface RemedialActionHumanEdit {
  action_id: string;
  deleted: boolean;
  created: boolean;
  title: string;
  description: string;
  role: CaseRole | null;
  priority: RemedialActionPriority;
  rule_ids: string[];
  edited_at: string | null;
  /**
   * When set, the server soft-removed this action because linked rules no longer fail or are unclear.
   * Absent or null for human removals and sticky restores.
   */
  auto_removed_reason?: "rules_resolved" | null;
}

export type RemedialActionHumanEditPatch = Partial<RemedialActionHumanEdit> & {
  action_id: string;
  clear?: boolean;
};

export interface ClearedPriorReviewReason {
  source: string;
  at?: string | null;
}

export interface ClearedPriorReview {
  prior_review_run_id: string;
  cleared_at: string;
  reason?: ClearedPriorReviewReason | null;
  prior_current_step: GuidedReviewStep;
  prior_reviewer_email: string;
  rule_overrides: number;
  disputes: number;
  ps_signoffs: number;
  remedial_edits: number;
  had_completeness_decision: boolean;
  acknowledged_at?: string | null;
  acknowledged_by: string;
}

export type PsRuleHumanReviewRunStatus = "active" | "superseded" | "saved";
export type ArchivedReviewReason = "rerun" | "start_over" | "restore";

export interface PsRuleHumanReviewRun {
  id: string;
  case_id: string;
  problem_statement_run_id: string;
  catalog_version: number;
  status: PsRuleHumanReviewRunStatus;
  title?: string;
  archived_reason?: ArchivedReviewReason | null;
  reviews: PsRuleHumanReview[];
  current_step: GuidedReviewStep;
  completed_steps: GuidedReviewStep[];
  completeness_review: GuidedCompletenessReview | null;
  checklist_disputes: Record<string, string>;
  remedial_edits: RemedialActionHumanEdit[];
  step_completions?: GuidedReviewStepCompletion[];
  audit_events?: GuidedReviewAuditEvent[];
  created_by: string;
  created_by_email?: string;
  created_at: string;
  updated_at: string | null;
  superseded_at: string | null;
  cleared_prior_review?: ClearedPriorReview | null;
}

export interface GuidedReviewSessionPatch {
  current_step?: GuidedReviewStep;
  completed_steps?: GuidedReviewStep[];
  completeness_review?: GuidedCompletenessReview | null;
  checklist_disputes?: Record<string, string>;
  clear_completeness_review?: boolean;
  acknowledge_cleared_prior_review?: boolean;
  title?: string;
}

export const getProblemStatementRollup = async (caseId: string): Promise<{
  case_id: string;
  client_name: string;
  case_type: string;
  catalog_version: number;
  rollups: ProblemStatementRollup[];
}> => {
  return apiRequest({
    method: "GET",
    url: `/case-checking/cases/${caseId}/problem-statements/rollup`,
  });
};

export const listProblemStatementRuns = async (
  caseId: string,
  limit = 20,
): Promise<ProblemStatementRun[]> => {
  return dedupeCaseDetailRequest(`problem-statement-runs:${caseId}:${limit}`, () =>
    apiRequest<ProblemStatementRun[]>({
      method: "GET",
      url: `/case-checking/cases/${caseId}/problem-statement-runs`,
      params: { limit },
    }),
  );
};

export const patchProblemStatementHumanReviews = async (
  caseId: string,
  runId: string,
  reviews: ProblemStatementHumanReviewPatch[],
): Promise<ProblemStatementRun> => {
  return apiRequest<ProblemStatementRun>({
    method: "PATCH",
    url: `/case-checking/cases/${caseId}/problem-statement-runs/${runId}/human-reviews`,
    data: { reviews },
  });
};

export const getActivePsRuleHumanReviewRun = async (
  caseId: string,
  problemStatementRunId: string,
  createIfMissing = true,
): Promise<PsRuleHumanReviewRun> => {
  return dedupeCaseDetailRequest(
    `ps-rule-review:${caseId}:${problemStatementRunId}:${createIfMissing ? "create" : "read"}`,
    () =>
      apiRequest<PsRuleHumanReviewRun>({
        method: "GET",
        url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/active`,
        params: { problem_statement_run_id: problemStatementRunId, create_if_missing: createIfMissing },
      }),
  );
};

export const createPsRuleHumanReviewRun = async (
  caseId: string,
  problemStatementRunId: string,
  supersedeExisting = true,
): Promise<PsRuleHumanReviewRun> => {
  invalidateCaseDetailRequestPrefix(`ps-rule-review:${caseId}:`);
  return apiRequest<PsRuleHumanReviewRun>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs`,
    data: { problem_statement_run_id: problemStatementRunId, supersede_existing: supersedeExisting },
  });
};

export const patchPsRuleHumanReviews = async (
  caseId: string,
  reviewRunId: string,
  reviews: PsRuleHumanReviewPatch[],
): Promise<PsRuleHumanReviewRun> => {
  return apiRequest<PsRuleHumanReviewRun>({
    method: "PATCH",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}/reviews`,
    data: { reviews },
  });
};

export const patchGuidedReviewSession = async (
  caseId: string,
  reviewRunId: string,
  patch: GuidedReviewSessionPatch,
): Promise<PsRuleHumanReviewRun> => {
  return apiRequest<PsRuleHumanReviewRun>({
    method: "PATCH",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}/session`,
    data: patch,
  });
};

export const patchRemedialActionHumanEdits = async (
  caseId: string,
  reviewRunId: string,
  edits: RemedialActionHumanEditPatch[],
): Promise<PsRuleHumanReviewRun> => {
  return apiRequest<PsRuleHumanReviewRun>({
    method: "PATCH",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}/remedial-edits`,
    data: { edits },
  });
};

export const resetPsRuleHumanReviewRun = async (
  caseId: string,
  reviewRunId: string,
): Promise<PsRuleHumanReviewRun> => {
  return apiRequest<PsRuleHumanReviewRun>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}/reset`,
  });
};

export const listPsRuleHumanReviewRuns = async (
  caseId: string,
  status?: string,
): Promise<PsRuleHumanReviewRun[]> => {
  return apiRequest<PsRuleHumanReviewRun[]>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs`,
    params: status ? { status } : undefined,
  });
};

export const deletePsRuleHumanReviewRun = async (caseId: string, reviewRunId: string): Promise<void> => {
  await apiRequest({
    method: "DELETE",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}`,
  });
};

export interface RestoreWillRecover {
  reviews: number;
  checklist_disputes: number;
  remedial_edits: number;
  completeness_review: boolean;
  completed_steps: number;
}

export interface RestoreWillOverwrite {
  review_run_id: string;
  review_count: number;
  current_step: GuidedReviewStep;
  has_progress: boolean;
}

export interface DiffSide {
  call: string | null;
  notes: string;
  summary: string;
}

export type DiffItemKind = "added" | "removed" | "changed";

export interface DiffItem {
  kind: DiffItemKind;
  key: string;
  label: string;
  group_key: string | null;
  group_label: string | null;
  selected: DiffSide | null;
  current: DiffSide | null;
}

export interface DiffSection {
  items: DiffItem[];
  unchanged_count: number;
}

export interface ProgressComparison {
  selected_step: GuidedReviewStep;
  current_step: GuidedReviewStep;
  selected_completed: GuidedReviewStep[];
  current_completed: GuidedReviewStep[];
}

export type GuidedReviewRelation =
  | "identical"
  | "progress_only"
  | "earlier"
  | "later"
  | "diverged";

export interface GuidedReviewDiffSections {
  rules: DiffSection;
  checklist: DiffSection;
  completeness: DiffSection;
  signoffs: DiffSection;
  remedials: DiffSection;
}

export interface GuidedReviewDifferences {
  is_current_review: boolean;
  has_differences: boolean;
  relation: GuidedReviewRelation;
  progress: ProgressComparison;
  sections: GuidedReviewDiffSections;
  not_restorable_rule_ids: string[];
}

export interface GuidedReviewRestorePreview {
  source_review_run_id: string;
  latest_problem_statement_run_id: string;
  will_recover: RestoreWillRecover;
  dropped_rule_ids: string[];
  will_overwrite: RestoreWillOverwrite | null;
  differences: GuidedReviewDifferences;
}

export const getGuidedReviewRestorePreview = async (
  caseId: string,
  sourceReviewRunId: string,
): Promise<GuidedReviewRestorePreview> => {
  return apiRequest<GuidedReviewRestorePreview>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/restore-preview`,
    params: { source_review_run_id: sourceReviewRunId },
  });
};

export interface GuidedReviewRestoreRequest {
  prior_review_run_id: string;
  include_human_reviews_on_ps_run?: boolean;
}

export const restoreGuidedReview = async (
  caseId: string,
  body: GuidedReviewRestoreRequest,
): Promise<PsRuleHumanReviewRun> => {
  invalidateCaseDetailRequestPrefix(`ps-rule-review:${caseId}:`);
  return apiRequest<PsRuleHumanReviewRun>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/restore`,
    data: body,
  });
};

export const saveGuidedReviewCopy = async (
  caseId: string,
  reviewRunId: string,
  title: string,
): Promise<PsRuleHumanReviewRun> => {
  return apiRequest<PsRuleHumanReviewRun>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}/copies`,
    data: { title },
  });
};

export interface ApplyPendingCarryRestoreResponse {
  applied: boolean;
  skipped: boolean;
  reason: string;
  review_run_id: string | null;
}

export const applyPendingCarryRestore = async (caseId: string): Promise<ApplyPendingCarryRestoreResponse> => {
  invalidateCaseDetailRequestPrefix(`ps-rule-review:${caseId}:`);
  return apiRequest<ApplyPendingCarryRestoreResponse>({
    method: "POST",
    url: `/case-checking/cases/${caseId}/ps-rule-human-review-runs/apply-pending-carry`,
  });
};

export const downloadProblemStatementsAgentDocx = async (
  caseId: string,
  fallbackName: string = "coverage_review_agent.docx",
  runId?: string,
): Promise<void> => {
  const qs = runId ? `?run_id=${encodeURIComponent(runId)}` : "";
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/problem-statements-agent-docx${qs}`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );
};

export const viewProblemStatementsAgentPdf = async (caseId: string, runId?: string): Promise<void> => {
  await openCaseCheckingExportPdf(
    caseCheckingDocxExportPdfUrl(caseId, "problem-statements-agent-docx", { runId }),
  );
};

export const downloadProblemStatementsComparisonDocx = async (
  caseId: string,
  fallbackName: string = "coverage_review_comparison.docx",
  runId?: string,
): Promise<void> => {
  const qs = runId ? `?run_id=${encodeURIComponent(runId)}` : "";
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/problem-statements-comparison-docx${qs}`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );
};

export const viewProblemStatementsComparisonPdf = async (
  caseId: string,
  runId?: string,
): Promise<void> => {
  await openCaseCheckingExportPdf(
    caseCheckingDocxExportPdfUrl(caseId, "problem-statements-comparison-docx", { runId }),
  );
};

export const downloadReviewSummaryXlsx = async (
  caseId: string,
  fallbackName: string = "review_summary.xlsx",
  runId?: string,
): Promise<void> => {
  const qs = runId ? `?run_id=${encodeURIComponent(runId)}` : "";
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/review-summary-xlsx${qs}`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  );
};

export const downloadReviewDeepdiveXlsx = async (
  caseId: string,
  fallbackName: string = "review_deepdive.xlsx",
  runId?: string,
): Promise<void> => {
  const qs = runId ? `?run_id=${encodeURIComponent(runId)}` : "";
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/review-deepdive-xlsx${qs}`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  );
};

export const downloadGuidedReviewAuditCsv = async (
  caseId: string,
  reviewRunId: string,
  fallbackName: string = "guided_review_audit.csv",
): Promise<void> => {
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/ps-rule-human-review-runs/${reviewRunId}/export/audit-csv`,
    fallbackName,
    "text/csv",
  );
};

export const downloadAdviceQualityFileReviewDocx = async (
  caseId: string,
  fallbackName: string = "advice_quality_file_review.docx",
  psRunId?: string,
  reviewRunId?: string,
): Promise<void> => {
  const params = new URLSearchParams();
  if (psRunId) params.set("run_id", psRunId);
  if (reviewRunId) params.set("review_run_id", reviewRunId);
  const qs = params.toString() ? `?${params.toString()}` : "";
  await _downloadCaseCheckingBlob(
    `/case-checking/cases/${caseId}/export/advice-quality-file-review-docx${qs}`,
    fallbackName,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );
};

/** Open Advice Quality File Review as PDF in a new browser tab (LibreOffice conversion on the API). */
export const viewAdviceQualityFileReviewPdf = async (
  caseId: string,
  psRunId?: string,
  reviewRunId?: string,
): Promise<void> => {
  await openCaseCheckingExportPdf(
    caseCheckingDocxExportPdfUrl(caseId, "advice-quality-file-review-docx", {
      runId: psRunId,
      reviewRunId,
    }),
  );
};

// ============================================
// Assessment History (Audit Trail)
// ============================================

export interface AssessmentRecord {
  id: string;
  case_id: string;
  assessment: Assessment;
  run_by: string | null;
  run_type: "manual" | "auto" | "scheduled";
  use_llm: boolean;
  document_count: number;
  case_type: CaseType | null;
  rule_set_version: number | null;
  created_at: string;
  duration_ms: number | null;
}

/**
 * Get assessment history for a case.
 * Returns all assessment runs in reverse chronological order (newest first).
 */
export const getAssessmentHistory = async (
  caseId: string,
  limit: number = 50
): Promise<AssessmentRecord[]> => {
  const response = await apiRequest<AssessmentRecord[]>({
    method: "GET",
    url: `/case-checking/cases/${caseId}/assessments`,
    params: { limit },
  });
  return response;
};

// ============================================
// Portfolio Insights (cross-case dashboard)
// ============================================

export type PortfolioRag = "red" | "amber" | "green" | "grey";

export interface PortfolioCaseColumn {
  case_id: string;
  client_name: string;
  case_type: string;
  grade: CaseGrade | null;
}

export interface PortfolioCell {
  case_id: string;
  status: string | null;
  rag: PortfolioRag | null;
}

export interface PortfolioRuleRow {
  rule_id: string;
  label: string;
  group_name: string;
  cells: PortfolioCell[];
  fail_count: number;
  unclear_count: number;
}

export interface ConsistentFailureRow {
  rule_id: string;
  label: string;
  group_name: string;
  cases_failed: number;
  applicable_count: number;
  also_unclear: number;
  failing_clients: string[];
}

export interface PortfolioTotals {
  cases: number;
  rules: number;
  passed: number;
  unclear: number;
  failed: number;
  na: number;
}

export interface GroupBreakdown {
  group_name: string;
  failed: number;
  unclear: number;
  passed: number;
}

export interface PortfolioAssessment {
  columns: PortfolioCaseColumn[];
  rows: PortfolioRuleRow[];
  consistent_failures: ConsistentFailureRow[];
  group_breakdown: GroupBreakdown[];
  totals: PortfolioTotals;
  rule_set_versions: number[];
}

export interface InsightTrend {
  title: string;
  /** Plain-language interpretation (preferred). */
  insight: string;
  /** One quantified line, e.g. "14 of 24 cases". */
  stat?: string;
  severity: "critical" | "warning" | "info";
  /** @deprecated legacy shape */
  detail?: string;
}

export interface PortfolioInsights {
  headline: string;
  trends: InsightTrend[];
  priorities: string[];
  cache_key: string;
  generated_at: string;
}

/** Build repeated `case_ids=` query params (FastAPI List[str] style). */
const _caseIdsQuery = (caseIds?: string[]): string => {
  if (!caseIds || caseIds.length === 0) return "";
  return "?" + caseIds.map((id) => `case_ids=${encodeURIComponent(id)}`).join("&");
};

/** Rules x cases matrix + consistent-failures rollup. Omit caseIds for all assessed cases. */
export const getPortfolioAssessment = async (
  caseIds?: string[]
): Promise<PortfolioAssessment> => {
  return apiRequest<PortfolioAssessment>({
    method: "GET",
    url: `/case-checking/portfolio/assessment${_caseIdsQuery(caseIds)}`,
  });
};

/** LLM-assisted digest for the selection (cached unless `refresh`). */
export const getPortfolioInsights = async (
  caseIds?: string[],
  refresh: boolean = false
): Promise<PortfolioInsights> => {
  const query = _caseIdsQuery(caseIds);
  const sep = query ? "&" : "?";
  return apiRequest<PortfolioInsights>({
    method: "POST",
    url: `/case-checking/portfolio/insights${query}${sep}refresh=${refresh}`,
  });
};

export interface PortfolioProblemStatementColumn {
  case_id: string;
  client_name: string;
  case_type: string;
  grade: string | null;
  has_run: boolean;
  reviewed_count: number;
  scorable_count: number;
}

export interface PortfolioProblemStatementCell {
  case_id: string;
  rating: string | null;
  rag: string | null;
  agent_rating: string | null;
  human_reviewed: boolean;
}

export interface PortfolioProblemStatementEvidence {
  case_id: string;
  client_name: string;
  rating: string;
  text: string;
}

export interface PortfolioProblemStatementRow {
  problem_statement_id: string;
  title: string;
  cells: PortfolioProblemStatementCell[];
  pass_count: number;
  unclear_count: number;
  fail_count: number;
  na_count: number;
  applicable_count: number;
  fail_rate_pct: number;
  issue_rate_pct: number;
  evidence: PortfolioProblemStatementEvidence[];
}

export interface PortfolioProblemStatementHotspot {
  problem_statement_id: string;
  title: string;
  cases_with_issue: number;
  applicable_count: number;
  fail_count: number;
  unclear_count: number;
  fail_rate_pct: number;
  issue_rate_pct: number;
  affected_clients: string[];
}

export interface PortfolioProblemStatementTotals {
  cases: number;
  cases_with_runs: number;
  statements: number;
  passed: number;
  unclear: number;
  failed: number;
  na: number;
  human_reviewed_cells: number;
  human_overrides: number;
  overall_pass_rate_pct: number;
}

export interface PortfolioProblemStatements {
  columns: PortfolioProblemStatementColumn[];
  rows: PortfolioProblemStatementRow[];
  hotspots: PortfolioProblemStatementHotspot[];
  totals: PortfolioProblemStatementTotals;
  cases_missing_run: string[];
}

export interface ProblemStatementKeyRisk {
  problem_statement_id: string;
  title: string;
  insight: string;
  stat?: string;
  severity: "critical" | "warning" | "info";
}

export interface PortfolioProblemStatementInsights {
  headline: string;
  key_risks: ProblemStatementKeyRisk[];
  statement_insights: Record<string, string>;
  priorities: string[];
  cache_key: string;
  generated_at: string;
}

/** Progeny problem-statement × case matrix for portfolio insights. */
export const getPortfolioProblemStatements = async (
  caseIds?: string[],
): Promise<PortfolioProblemStatements> => {
  return apiRequest<PortfolioProblemStatements>({
    method: "GET",
    url: `/case-checking/portfolio/problem-statements${_caseIdsQuery(caseIds)}`,
  });
};

/** LLM-assisted management digest for Progeny coverage review (cached unless `refresh`). */
export const getPortfolioProblemStatementInsights = async (
  caseIds?: string[],
  refresh: boolean = false,
): Promise<PortfolioProblemStatementInsights> => {
  const query = _caseIdsQuery(caseIds);
  const sep = query ? "&" : "?";
  return apiRequest<PortfolioProblemStatementInsights>({
    method: "POST",
    url: `/case-checking/portfolio/problem-statements/insights${query}${sep}refresh=${refresh}`,
  });
};
